import { useEffect, useState } from '@wordpress/element';
import { Link, useNavigate } from 'react-router-dom';
import AdSlot from '../components/AdSlot';
import { usePlayer } from '../components/player/playerContext';
import { fetchJson } from '../utils/fetch';
import useGiveawayPlacements from '../features/giveaways/useGiveawayPlacements';
import GiveawaySlotSticker from '../features/giveaways/GiveawaySlotSticker';

import TrackCard from '../components/TrackCard';
import PlaylistTrackGrid from '../components/PlaylistTrackGrid';
import TrackOfTheDaySlot from '../components/TrackOfTheDaySlot';
import TrackFeatureSection from '../components/TrackFeatureSection';
import FeaturedArtistsSection from '../components/FeaturedArtistsSection';
import { Icon } from '../components/Icons';

const emptyChart = {
    title: '',
    subtitle: '',
    tracks: [],
};

function normalizeTracks(data) {
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.tracks)) {
        return data.tracks;
    }

    return [];
}

export default function MusicHomePage() {
    const {
        playPlaylist,
        playGeneratedQueue,
    } = usePlayer();

    const [recentlyAdded, setRecentlyAdded] = useState({
        title: 'Recently Added',
        subtitle: 'Fresh uploads from Bajantube artists',
        tracks: [],
    });

    const [seasonHits, setSeasonHits] = useState({
        title: 'Most Listened To',
        subtitle: 'The biggest songs of the Crop Over season',
        tracks: [],
    });

    const [trending, setTrending] = useState({
        title: 'Trending Now',
        subtitle: 'Tracks gaining momentum right now',
        tracks: [],
    });

    const [trackOfTheDay, setTrackOfTheDay] = useState(null);
    const [featuredTracks, setFeaturedTracks] = useState([]);
    const [featuredArtists, setFeaturedArtists] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const placements = useGiveawayPlacements();

    useEffect(() => {
        async function loadHomePageData() {
            setIsLoading(true);

            const [
                seasonHitsData,
                trendingData,
                recentlyAddedData,
                trackOfTheDayData,
                featuredTracksData,
                featuredArtistsData,
            ] = await Promise.all([
                fetchJson('/wp-json/bajantube/v1/charts/top-10-season-hits', emptyChart),
                fetchJson('/wp-json/bajantube/v1/charts/trending', emptyChart),
                fetchJson('/wp-json/bajantube/v1/music/tracks?per_page=8', []),
                fetchJson('/wp-json/bajantube/v1/featured/track-of-the-day', null),
                fetchJson('/wp-json/bajantube/v1/featured/tracks', []),
                fetchJson('/wp-json/bajantube/v1/featured/artists', []),
            ]);

            setRecentlyAdded({
                title: 'Recently Added',
                subtitle: 'Fresh uploads from Bajantube artists',
                tracks: normalizeTracks(recentlyAddedData),
            });

            setSeasonHits({
                ...seasonHitsData,
                tracks: seasonHitsData?.tracks || [],
            });

            setTrending({
                ...trendingData,
                tracks: trendingData?.tracks || [],
            });

            setTrackOfTheDay(trackOfTheDayData || null);
            setFeaturedTracks(normalizeTracks(featuredTracksData));
            setFeaturedArtists(Array.isArray(featuredArtistsData) ? featuredArtistsData : []);

            setIsLoading(false);
        }

        loadHomePageData();
    }, []);

    function playSingleTrack(track, queueTracks = recentlyAdded.tracks) {
        playGeneratedQueue({
            clickedTrack: track,
            trackOfTheDay,
            tracks: queueTracks,
        });
    }

    return (
        <main className="bjnt-home-page">
            <AdSlot
                placement="top_banner"
                type="hero"
                label="Top Banner"
            />

            {(placements?.homepage?.length > 0) && (
                <GiveawaySlotSticker
                    placement={placements.homepage}
                    slot="homepage"
                    sourceType="homepage"
                />
            )}


            <section className="bjnt-section">
                <PlaylistSection
                    title={recentlyAdded.title}
                    tracks={recentlyAdded.tracks}
                    isLoading={isLoading}
                    variant="playlist"
                    limit={8}
                    viewAllUrl="/discover"
                    hidePlayButton={true}
                    onPlayTrack={(track) => playPlaylist({
                        title: 'Recently Added',
                        tracks: [track],
                    })}
                />
            </section>

            <TrackOfTheDaySlot
                track={trackOfTheDay}
                onPlayTrack={(track) =>
                    playSingleTrack(track, [...featuredTracks, ...recentlyAdded.tracks])
                }
            />

            <section className="bjnt-section bjnt-section--with-sidebar">
                <div>
                    <PlaylistSection
                        title={seasonHits.title}
                        tracks={seasonHits.tracks}
                        isLoading={isLoading}
                        variant="chart"
                        limit={10}
                        onPlayPlaylist={() => playPlaylist(seasonHits)}
                        onPlayTrack={(track, index) =>
                            playPlaylist({
                                ...seasonHits,
                                startIndex: index,
                            })
                        }
                    />
                </div>

                <div className="bjnt-sidebar-ads">
                    <AdSlot
                        placement="site_ad"
                        type="portrait"
                        label="Site Ad"
                    />
                </div>
            </section>

            <section className="bjnt-home-section">
                <TrackFeatureSection
                    tracks={featuredTracks}
                    isLoading={isLoading}
                    onPlayTrack={(track) => playSingleTrack(track, featuredTracks)}
                />
            </section>

            <AdSlot
                placement="site_ad"
                type="landscape"
                label="Site Ad"
            />

            {/* <div className="rudeboy-banner">
                <img src="/wp-content/uploads/2026/06/rude-boy-min-1.jpg" alt="" />
            </div> */}

            <section className="bjnt-home-section">
                <FeaturedArtistsSection featuredArtists={featuredArtists} />
            </section>

            <AdSlot
                placement="site_ad"
                type="landscape"
                label="Site Ad"
            />

            <section className="bjnt-home-section">
                <PlaylistSection
                    title={trending.title}
                    tracks={trending.tracks}
                    isLoading={isLoading}
                    variant="chart"
                    limit={5}
                    onPlayPlaylist={() => playPlaylist(trending)}
                    onPlayTrack={(track, index) =>
                        playPlaylist({
                            ...trending,
                            startIndex: index,
                        })
                    }
                />


            </section>

            <AdSlot placement="site_ad" type="landscape" />
        </main>
    );
}

function HomeHero({ seasonHits, playPlaylist }) {
    const navigate = useNavigate();

    function handleSearchSubmit(event) {
        event.preventDefault();

        const formData = new FormData(event.currentTarget);
        const query = String(formData.get('q') || '').trim();

        if (query) {
            navigate(`/search?q=${encodeURIComponent(query)}`);
        }
    }
    return (
        <section className="bjnt-home-hero">
            <div className="bjnt-home-copy">

                <p className="bjnt-home-subtitle">
                    Stream new releases, discover Bajan artists, and follow the
                    hottest tracks across Barbados.
                </p>

                <form className="bjnt-home-search" onSubmit={handleSearchSubmit}>
                    <input
                        type="search"
                        name="q"
                        placeholder="Search songs..."
                        aria-label="Search music"
                    />

                    <button type="submit">Search</button>
                </form>

                <div className="bjnt-home-actions">
                    <Link to="/upload" className="bjnt-home-btn">
                        Upload Your Music
                    </Link>

                    <button
                        type="button"
                        className="bjnt-home-btn bjnt-home-btn--secondary"
                        disabled={!seasonHits.tracks.length}
                        onClick={() => playPlaylist(seasonHits)}
                    >
                        Play Season Hits
                    </button>
                </div>
            </div>

            <div className="bjnt-upload-logo bjnt-upload-logo--image">
                <img
                    src="/wp-content/plugins/bajantube/assets/img/bajantube-icon.png"
                    alt="Bajantube"
                />
            </div>
        </section>
    );
}

function PlaylistSection({
    title,
    subtitle,
    tracks = [],
    isLoading,
    variant = 'playlist',
    limit = 5,
    hidePlayButton = false,
    viewAllUrl,
    onPlayPlaylist,
    onPlayTrack,
}) {
    const visibleTracks = tracks.slice(0, limit);
    const isChart = variant === 'chart';

    return (
        <>
            <div className="bjnt-section-header">
                <div>
                    <h2 className="bjnt-section-title">{title}</h2>

                    {subtitle && (
                        <p className="bjnt-section-subtitle">
                            {subtitle}
                        </p>
                    )}
                </div>

                {!hidePlayButton && (
                    <button
                        type="button"
                        className="bjnt-playlist-play"
                        disabled={!tracks.length}
                        onClick={onPlayPlaylist}
                    >
                        <Icon name="play" />
                        <span>Play {isChart ? 'Chart' : 'Playlist'}</span>
                    </button>
                )}
            </div>

            {isLoading && (
                <p className="bjnt-empty-state">Loading tracks...</p>
            )}

            {!isLoading && !tracks.length && (
                <p className="bjnt-empty-state">No published tracks yet.</p>
            )}

            {!!tracks.length && isChart && (
                <div className="bjnt-chart-list">
                    {visibleTracks.map((track, index) => (
                        <TrackCard
                            key={track.id}
                            variant="chart"
                            rank={index + 1}
                            track={track}
                            index={index}
                            onPlayTrack={onPlayTrack}
                        />
                    ))}
                </div>
            )}

            {!!tracks.length && !isChart && (
                <PlaylistTrackGrid
                    tracks={visibleTracks}
                    onPlayTrack={onPlayTrack}
                />
            )}

            {viewAllUrl && (
                <div className="bjnt-section-footer">
                    <Link
                        to={viewAllUrl}
                        className="bjnt-section-discover-more"
                    >
                        Discover More
                        <Icon name="arrow-right" />
                    </Link>
                </div>
            )}
        </>
    );
}